import { ICD10CM_SYSTEM } from "@/lib/diagnosisApi";

export const PMH_KIND = {
  condition: "condition",
  pertinentNegative: "pertinent_negative",
};

export const PMH_SOURCES = [
  { id: "patient", label: "Patient" },
  { id: "family", label: "Family" },
  { id: "record", label: "Record" },
  { id: "other", label: "Other" },
];

/** Short chips for the right-hand Quick Entry panel. Codes are ICD-10 snapshots. */
export const QUICK_ENTRY_ITEMS = [
  { code: "I10", chip: "Hypertension", description: "Essential (primary) hypertension" },
  { code: "E11.9", chip: "Diabetes mellitus", description: "Type 2 diabetes mellitus without complications" },
  { code: "E78.5", chip: "Hyperlipidemia", description: "Hyperlipidemia, unspecified" },
  {
    code: "I25.10",
    chip: "Coronary artery disease",
    description: "Atherosclerotic heart disease of native coronary artery without angina pectoris",
  },
  { code: "J44.9", chip: "COPD", description: "Chronic obstructive pulmonary disease, unspecified" },
  { code: "K21.9", chip: "GERD", description: "Gastro-esophageal reflux disease without esophagitis" },
  { code: "F32.9", chip: "Depression", description: "Major depressive disorder, single episode, unspecified" },
  { code: "F41.9", chip: "Anxiety", description: "Anxiety disorder, unspecified" },
];

export const PERTINENT_NEGATIVE_ITEMS = [
  { id: "no-stroke", description: "No history of stroke" },
  { id: "no-mi", description: "No history of myocardial infarction" },
  { id: "no-malignancy", description: "No history of malignancy" },
  { id: "no-seizures", description: "No history of seizures" },
];

export function sourceLabel(source) {
  return PMH_SOURCES.find((row) => row.id === source)?.label || "Patient";
}

export function formatPmhDiagnosis(item) {
  const code = item?.code || "";
  const description = item?.display || item?.description || "";
  if (code && description) return `${description} (${code})`;
  return description || code || "—";
}

export function formatSearchDiagnosis(item) {
  const code = item?.code || "";
  const description = item?.description || "";
  if (code && description) return `${code} — ${description}`;
  return description || code;
}

export function formatOnsetDate(onsetDate, birthDate) {
  if (!onsetDate) return "—";
  const yearMatch = String(onsetDate).match(/^(\d{4})/);
  const year = yearMatch ? Number(yearMatch[1]) : null;
  const birthYear = birthDate ? Number(String(birthDate).slice(0, 4)) : null;
  if (year && birthYear && year >= birthYear) {
    return `${onsetDate} (${year - birthYear} y.o.)`;
  }
  return onsetDate;
}

export function formatReviewedAt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function toPmhConditionPayload(item, extras = {}) {
  return {
    kind: PMH_KIND.condition,
    code: item.code,
    system: item.system || item.codeSystem || ICD10CM_SYSTEM,
    description: item.description || item.display || item.code,
    display: item.description || item.display || item.code,
    diagnosisCodeId: item.id || item.diagnosisCodeId || null,
    source: extras.source || "patient",
    onsetDate: extras.onsetDate || null,
    comment: extras.comment || null,
  };
}

export function toPmhNegativePayload(item) {
  return {
    kind: PMH_KIND.pertinentNegative,
    description: item.description || item.display,
    source: "patient",
  };
}

export function conditionsFromItems(items = []) {
  return (items || []).filter((row) => row.kind !== PMH_KIND.pertinentNegative);
}

export function negativesFromItems(items = []) {
  return (items || []).filter((row) => row.kind === PMH_KIND.pertinentNegative);
}
